import React from 'react';
import { ToolContainer } from './ToolContainer';
import type { ToolId } from '../utils/slugs';
import { InfoIcon } from '../Icons';
import { MeasurementConverterClient } from './ui/MeasurementConverterClient';

interface MeasurementConverterProps {
    setActiveTool: (toolId: ToolId) => void;
    breadcrumbs: React.ReactNode;
}

export const MeasurementConverter: React.FC<MeasurementConverterProps> = ({ setActiveTool, breadcrumbs }) => {
    return (
        <ToolContainer
            title="Baking Measurement Converter"
            description="The ultimate baking converter for professional results. Convert cups to grams, ounces to milliliters, and more for over 25 common baking ingredients."
            setActiveTool={setActiveTool}
            breadcrumbs={breadcrumbs}
            toolId="measurement-converter"
        >
            <MeasurementConverterClient />
            <MeasurementConverterContent />
        </ToolContainer>
    );
};

const MeasurementConverterContent: React.FC = () => (
    <div className="mt-12 space-y-12 pt-8 border-t border-brand-border">
        <div className="prose prose-pink max-w-none">
            <h2 className="font-serif text-3xl font-bold text-brand-text-title mb-4">Professional Baking Conversions: Cups to Grams & More</h2>
            <p className="text-lg text-brand-text-body/80 leading-relaxed">
                Baking is chemistry. A few extra grams of flour can turn a light cake into a dense brick. Our **Baking Measurement Converter** is designed for precision. Easily convert **cups to grams**, **tablespoons to milliliters**, and **ounces to grams**. Whether you are using **all-purpose flour**, **sugar**, or **butter**, this tool handles the specific densities of over 25 ingredients to ensure your recipe turns out perfectly.
            </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title flex items-center gap-2">
                    <InfoIcon className="w-6 h-6 text-brand-pink" />
                    How to Use
                </h3>
                <ol className="space-y-3 text-sm text-brand-text-body/80 list-decimal pl-4 marker:text-brand-pink marker:font-bold">
                    <li><strong>Select Ingredient:</strong> Choose the specific ingredient you are converting.</li>
                    <li><strong>Enter Amount:</strong> Type the quantity you have.</li>
                    <li><strong>Choose Units:</strong> Select the unit you are starting with and the unit you want.</li>
                </ol>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title">Frequently Asked Questions</h3>
                <div className="space-y-4">
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            Why is weighing better than cups?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            Volume measurements (cups) are notoriously inaccurate. Weighing in <strong>grams</strong> ensures your recipe works every time.
                        </p>
                    </details>
                </div>
            </div>
        </div>
    </div>
);
